import clsx from 'clsx';
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useAuth } from '../../../lib/auth';
import AuthLayout from '../../layouts/AuthLayout';
import Skeleton from '../../../components/Skeleton';
import { PlanWeek } from '../../../components/plan/PlanWeek';
import { PlanTabs } from '../../../components/plan/PlanTabs';
import { PlanTeaserCard } from '../../../components/plan/PlanTeaserCard';
import { Button } from '../../../components/buttons/Button';
import { CURRENT_DAY_ANCHOR } from '../../../lib/plan/types';
import { usePlan } from '../../../lib/plan/usePlan';

const MAIN_CLASS = clsx(
    'relative',
    'mx-auto box-content max-w-2xl py-9',
    'px-4 sm:px-8 md:px-10'
);

export default function PlanPage() {
    const { userHasFullLicense } = useAuth({
        middleware: 'auth',
        redirectTo: '/',
        tmp: 'PlanPage',
    });

    const navigate = useNavigate();
    const { hash } = useLocation();
    const { plan, error, isLoading, mutate } = usePlan();
    // Odrolovat smíme až po načtení plánu (dřív ten prvek neexistuje) a jen
    // jednou — jinak by nás to po každém dokončeném cvičení házelo zpátky.
    const scrolledRef = useRef(false);

    useEffect(() => {
        if (!isLoading && plan === null) {
            navigate('/plan-na-miru/dotaznik');
        }
    }, [isLoading, plan, navigate]);

    useEffect(() => {
        if (scrolledRef.current || plan === null) return;
        if (hash !== `#${CURRENT_DAY_ANCHOR}`) return;

        const target = document.getElementById(CURRENT_DAY_ANCHOR);
        if (!target) return;

        scrolledRef.current = true;
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, [hash, plan]);

    if (isLoading || plan === null) {
        return (
            <AuthLayout>
                <main className={MAIN_CLASS}>
                    <Skeleton className="mb-6 h-64 w-full rounded-2xl" />
                    <Skeleton
                        count={5}
                        className="mb-6 h-16 w-full rounded-2xl"
                    />
                </main>
            </AuthLayout>
        );
    }

    // Plán se nepodařilo načíst — bez tohoto stavu skončí chyba stejně jako
    // prázdná odpověď, tedy prázdnou stránkou.
    if (error) {
        return (
            <AuthLayout>
                <main className={MAIN_CLASS}>
                    <h1 className="mb-6">Plán na míru</h1>
                    <p className="mb-6 text-ink-800">
                        Plán se nepodařilo načíst. Zkontrolujte prosím připojení
                        a zkuste to znovu.
                    </p>
                    <Button color="product" onClick={() => mutate()}>
                        Zkusit znovu
                    </Button>
                </main>
            </AuthLayout>
        );
    }

    // Uživatel ještě nevyplnil dotazník (API vrací 204). Nepřesměrováváme —
    // klik v menu má vést na stránku, která vysvětlí, co se má udělat.
    if (plan === null) {
        return (
            <AuthLayout>
                <main className={MAIN_CLASS}>
                    <h1 className="mb-6">Plán na míru</h1>
                    <p className="mb-8 text-ink-800">
                        Zatím tu žádný plán nemáte. Vyplňte krátký dotazník a
                        Všímavec sestaví plán cvičení pro vaši třídu.
                    </p>
                    <PlanTeaserCard
                        showBadge={false}
                        onStart={() => navigate('/plan-na-miru/dotaznik')}
                    />
                </main>
            </AuthLayout>
        );
    }

    const firstLockedWeek = plan.weeks.find((week) => week.locked);
    // U plné licence jsou budoucí týdny zamčené postupem, ne nákupem —
    // nabízet koupi tomu, kdo už zaplatil, je chyba.
    const lockReason = userHasFullLicense ? 'progress' : 'purchase';

    return (
        <AuthLayout>
            <main className={MAIN_CLASS}>
                <h1 className="mb-6">Plán na míru</h1>
                <div className="mb-10">
                    <PlanTabs />
                </div>
                <div className="grid gap-10">
                    {plan.weeks.map((week) => (
                        <PlanWeek
                            key={week.week}
                            week={week}
                            currentPosition={plan.current_position}
                            lockReason={lockReason}
                            showUpsell={week === firstLockedWeek}
                            onMutate={() => mutate()}
                        />
                    ))}
                </div>
            </main>
        </AuthLayout>
    );
}
